import React, { useCallback } from 'react'
import { useCanvasFiles } from '@/hooks/useCanvasFiles'
import { createImageFileNode, createVideoFileNode, createAudioFileNode, createTextFileNode } from '@/lib/canvas'
import type { ReactFlowInstance, Node } from '@xyflow/react'
import { toast } from '@/components/ui/toast'
import { GENIE_FILE_TYPE } from '@/components/canvas/nodes/utils/library-file-drop'
import { toUploadableAudio, unsupportedAudioMessage } from '@/components/canvas/nodes/variants/audio-file/audio-upload'
import { flowNodeStyle } from '@/lib/canvas/node-box'

type DropKind = 'image' | 'video' | 'audio' | 'text'

const TEXT_EXTENSIONS = /\.(txt|md|markdown|json|csv|yaml|yml)$/i

/**
 * A File Manager drag is an existing file, placed by the react-dnd drop target.
 * Chromium also attaches a dragged, CORS-readable thumbnail as a File, so it
 * must not be mistaken for an upload — nor stopped before react-dnd's window
 * listener sees it.
 */
const isLibraryFileDrag = (dataTransfer: DataTransfer) => dataTransfer.types.includes(GENIE_FILE_TYPE)

/**
 * Classify a dropped file into the node kind that can hold it, or null if
 * nothing on the canvas can. Text is matched by extension as well as MIME,
 * because browsers report .md/.yaml files as empty or octet-stream.
 */
function kindOf(file: File): DropKind | null {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  if (file.type.startsWith('audio/')) return 'audio'
  if (file.type.startsWith('text/') || TEXT_EXTENSIONS.test(file.name)) return 'text'
  return null
}

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
      URL.revokeObjectURL(img.src)
    }
    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      reject(new Error('Failed to load image'))
    }
    img.src = URL.createObjectURL(file)
  })
}

interface UseCanvasFileDropProps {
  canvasRef: React.RefObject<ReactFlowInstance>
  canvasId: string | null
  viewport: { x: number; y: number; zoom: number }
  canvasSize: { width: number; height: number }
  containerSelector: string
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>
}

/**
 * Custom hook for handling native browser file drops onto the canvas
 *
 * Features:
 * - Accepts image, video, audio and text files from the file system
 * - Converts screen coordinates to world coordinates
 * - Uploads files via backend API
 * - Creates ImageFile / VideoFile / AudioFile / TextFile nodes at drop position
 * - Integrates with auto-save system
 *
 * Usage:
 * ```tsx
 * const fileDropHandlers = useCanvasFileDrop({
 *   canvasRef,
 *   canvasId,
 *   viewport,
 *   canvasSize,
 *   containerSelector: '.canvas-page'
 * })
 *
 * <div
 *   className="canvas-page"
 *   onDragOver={fileDropHandlers.onDragOver}
 *   onDrop={fileDropHandlers.onDrop}
 * >
 *   <MagicCanvas ... />
 * </div>
 * ```
 */
export function useCanvasFileDrop({
  canvasRef,
  canvasId,
  viewport,
  canvasSize,
  containerSelector,
  setNodes,
}: UseCanvasFileDropProps) {
  const { uploadFile } = useCanvasFiles()

  /**
   * Handle dragover event - required to enable drop
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    // Only handle if dragging files
    if (e.dataTransfer.types.includes('Files') && !isLibraryFileDrag(e.dataTransfer)) {
      e.preventDefault()
      e.stopPropagation()
      e.dataTransfer.dropEffect = 'copy'
    }
  }, [])

  /**
   * Handle drop event - process files and create nodes
   */
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    // Check if files are being dropped
    if (!e.dataTransfer.files?.length || isLibraryFileDrag(e.dataTransfer)) {
      return
    }

    // Check if drop is over a node - if so, let the node handle it
    const target = e.target as HTMLElement
    const nodeElement = target.closest('[data-node-id]')
    if (nodeElement) {
      return
    }

    e.preventDefault()
    e.stopPropagation()

    if (!canvasId || !canvasRef.current) {
      toast.error('Canvas not ready')
      return
    }

    // Route by type: images, videos and audio upload to file nodes, text files are
    // read client-side into a TextFile node. Anything else is reported
    // honestly rather than silently dropped.
    const dropped = Array.from(e.dataTransfer.files)
    const rejected = dropped.filter(f => kindOf(f) === null)
    // Audio the upload API refuses (FLAC, AAC...) is reported now, before an empty node is placed for it;
    // aliases of accepted types are relabelled so the API takes them.
    const files: File[] = []
    for (const file of dropped) {
      const kind = kindOf(file)
      if (kind !== 'audio') {
        if (kind) files.push(file)
        continue
      }
      const audio = toUploadableAudio(file)
      if (audio) files.push(audio)
      else toast.error(unsupportedAudioMessage(file))
    }

    if (rejected.length) {
      const names = rejected.map(f => f.name.split('.').pop()?.toUpperCase() || 'this').join(', ')
      toast.error(`${names} files aren't supported on the canvas yet — drop images, videos, audio, or text files`)
    }

    if (!files.length) {
      return
    }

    // Get drop position
    const container = document.querySelector(containerSelector)
    if (!container) {
      console.error(`Container not found: ${containerSelector}`)
      return
    }

    const rect = container.getBoundingClientRect()
    const screenX = e.clientX - rect.left
    const screenY = e.clientY - rect.top

    // Convert screen coordinates to world coordinates
    const worldX = (screenX - canvasSize.width / 2 - viewport.x) / viewport.zoom
    const worldY = (screenY - canvasSize.height / 2 - viewport.y) / viewport.zoom

    // Convert a factory node into a React Flow node at the given position.
    const toRfNode = (newNode: ReturnType<typeof createImageFileNode>, position: { x: number; y: number }): Node => ({
      id: newNode.id,
      type: newNode.component?.type || newNode.type,
      position,
      data: {
        state: newNode.component?.state || {},
        ...(newNode.data || {}),
      },
      style: flowNodeStyle(newNode.component?.type || newNode.type, newNode.dimensions),
    })

    // Process each file. Multiple files are staggered vertically by 20px.
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const kind = kindOf(file) as DropKind
      const position = { x: worldX, y: worldY + i * 20 }

      // Text never touches the upload API — the TextFile node holds its
      // content directly in state.
      if (kind === 'text') {
        try {
          const text = await file.text()
          setNodes((prev) => [...prev, toRfNode(createTextFileNode(position, text), position)])
          toast.success(`${file.name} added`)
        } catch (error) {
          console.error('[FileDrop] ❌ Failed to read text file:', error)
          toast.error(`Failed to read ${file.name}`)
        }
        continue
      }

      const loadingToast = toast.loading(`Uploading ${file.name}...`)

      try {
        let newNode: ReturnType<typeof createImageFileNode>
        let metadata: { width: number; height: number } | undefined

        if (kind === 'image') {
          // Dimensions are read up front so the node is sized correctly
          // before the upload round-trip completes.
          metadata = await readImageDimensions(file)
          newNode = createImageFileNode(position, undefined, undefined, metadata)
        } else if (kind === 'audio') {
          newNode = createAudioFileNode(position)
        } else {
          newNode = createVideoFileNode(position)
        }

        // Inject canvasId into node state (required for file uploads)
        if (newNode.component?.state) {
          newNode.component.state.canvasId = canvasId
        }

        setNodes((prev) => [...prev, toRfNode(newNode, position)])

        const uploadedFile = await uploadFile(canvasId, {
          file,
          nodeId: newNode.id,
          metadata,
        })

        if (uploadedFile) {
          setNodes((nds) => nds.map(n =>
            n.id === newNode.id
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    state: {
                      ...(n.data.state as Record<string, unknown>),
                      fileUuid: uploadedFile.uuid,
                      fileMetadata: {
                        ...(uploadedFile.metadata || {}),
                        publicUrl: uploadedFile.publicUrl || '',
                        filename: uploadedFile.filename,
                        size: uploadedFile.size,
                        contentType: uploadedFile.contentType,
                      },
                    },
                  },
                }
              : n
          ))

          toast.dismiss(loadingToast)
          toast.success(`${file.name} uploaded successfully`)
        } else {
          toast.dismiss(loadingToast)
          throw new Error('Upload failed - no file returned')
        }
      } catch (error) {
        console.error('[FileDrop] ❌ Upload failed:', error)
        toast.dismiss(loadingToast)
        toast.error(`Failed to upload ${file.name}`)
      }
    }
  }, [canvasId, canvasRef, viewport, canvasSize, containerSelector, uploadFile, setNodes])

  return {
    onDragOver: handleDragOver,
    onDrop: handleDrop,
  }
}